<script setup>
import gsap from "gsap";

const { locale, t } = useI18n();
const localePath = useLocalePath();

const route = useRoute()
const router = useRouter()

const selectedTag = ref('')
const tags = ['networking', 'workshop', 'public', 'members']

const width = ref(0);
const eventsLimit = ref(6);
const eventPageLimit = ref(6);
const endCursor = ref(null);
const filteredTags = ref([]);
const filtering = ref(false);
const selectedMonth = ref(0);
const xTo = ref(null);
const yTo = ref(null);
// const featuredEvent = ref(null);

const months = [
  { value: 1, label: t('January') },
  { value: 2, label: t('February') },
  { value: 3, label: t('March') },
  { value: 4, label: t('April') },
  { value: 5, label: t('May') },
  { value: 6, label: t('June') },
  { value: 7, label: t('July') },
  { value: 8, label: t('August') },
  { value: 9, label: t('September') },
  { value: 10, label: t('October') },
  { value: 11, label: t('November') },
  { value: 12, label: t('December') },
];

useHeadMeta({
  title: t("events.meta.title"),
  description: t("events.meta.description"),
  url: route.fullPath,
  csUrl: 'cs/akce',
  enUrl: 'en/events',
  image: "https://be.opero.cz/wp-content/uploads/2024/03/3_Opero_eventspace_prague_summer_atrium.jpg",
  alt: "Opero",
});

const nextSixMonths = computed(() => {
  const currentMonth = new Date().getMonth() + 1
  const result = []

  for (let i = 0; i < 6; i++) {
    const monthValue = ((currentMonth + i - 1) % 12) + 1
    const monthLabel = months.find(m => m.value === monthValue)?.label || monthValue
    result.push({ value: monthValue, label: monthLabel })
  }

  return result
})

const cursor = ref("");
const cursorStyle = ref({});
const cursorOnClick = ref(() => {});

const updateWidth = () => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", {
      duration: 0.1,
      ease: "power3",
    });
    yTo.value = gsap.quickTo("#cursor", "y", {
      duration: 0.1,
      ease: "power3",
    });
  }
};

const getHeight = computed(() => {
  if (width.value > 1350) return 750;
  if (width.value > 1150) return 600;

  return width.value - 400;
});

// events query
const eventsQuery = gql`
  query getEvents(
    $limit: Int!
    $endCursor: String
    $locale: LanguageCodeFilterEnum!
    $categories: [String]
    $dateFrom: String
    $dateTo: String
  ) {
    events(
      first: $limit
      after: $endCursor
      where: {
        language: $locale
        upcomingOnly: true
        categorySlug: $categories
        dateFrom: $dateFrom
        dateTo: $dateTo
        orderby: { field: DATE, order: ASC }
      }
    ) {
      nodes {
        id
        eventId
        date
        eventTags {
          edges {
            node {
              name
            }
          }
        }
        title
        uri
        slug
        eventsConfig {
          title
          perex
          featuredImage {
            mediaItemUrl
          }
          datesettings {
            timeEnd
            timeFrom
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const queryVariables = {
  limit: eventsLimit.value,
  locale: locale.value.toString().toUpperCase(),
};
const { result, variables, loading, fetchMore, onResult } = useQuery(
  eventsQuery,
  queryVariables
);

const loadMore = () => {
  fetchMore({
    variables: {
      limit: eventPageLimit.value,
      locale: locale.value.toString().toUpperCase(),
      endCursor: endCursor.value,
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      // No new data
      if (!fetchMoreResult) return previousResult;

      // Concat previous data with new data
      return {
        ...previousResult,
        events: {
          nodes: [
            ...previousResult.events.nodes,
            ...fetchMoreResult.events.nodes,
          ],
          pageInfo: { ...fetchMoreResult.events.pageInfo },
        },
      };
    },
  });
};

const filterEvents = () => {
  eventsLimit.value = eventPageLimit.value;
  variables.value = {
    limit: eventsLimit.value,
    locale: locale.value.toString().toUpperCase(),
    endCursor: null,
    categories: filteredTags.value.length ? filteredTags.value : null,
    dateFrom: selectedMonth.value ? `1.${selectedMonth.value}.2025` : null,
    dateTo: selectedMonth.value ? `31.${selectedMonth.value}.2025` : null,
  };
  filtering.value = false;
};

const events = computed(() => result?.value?.events?.nodes ?? []);

const featuredEvent = computed(() => {
  return events.value?.[0] || null;
});

const hasMoreEvents = computed(
  () => result?.value?.events?.pageInfo?.hasNextPage
);

onResult((queryResult) => {
  endCursor.value = queryResult.data?.events?.pageInfo?.endCursor;
  // if (!featuredEvent.value)
  //   featuredEvent.value = queryResult.data?.events?.nodes[0];
});

const toggleTag = (tag) => {
  filtering.value = true;
  if (filteredTags.value.includes(tag)) {
    filteredTags.value = filteredTags.value.filter((item) => item !== tag);
  } else {
    filteredTags.value.push(tag);
  }
  setTimeout(() => {
    filterEvents();
  }, 500);
};

const filterMonth = () => {
  filtering.value = true;
  setTimeout(() => {
    filterEvents();
  }, 500);
};

const resetFilter = () => {
  filtering.value = true;
  filteredTags.value = [];
  selectedMonth.value = 0;
  setTimeout(() => {
    filterEvents();
  }, 500);
};

const setCursor = (value, onClick, style) => {
  cursor.value = value;
  if (cursorOnClick.value !== onClick) {
    window.document.removeEventListener("click", cursorOnClick.value);
    cursorOnClick.value = onClick;

    window.document.addEventListener("click", cursorOnClick.value);
  }
  if (cursorStyle.value !== style) {
    cursorStyle.value = style;
  }
};

const cursorAction = (slug) => {
  return navigateTo(localePath({ name: "event-id", params: { id: slug } }));
};

// const filteredEvents = ref([]);
// filteredEvents.value = events.value.filter(
//   (e) => e?.eventId !== featuredEvent?.eventId
// );

onMounted(() => {
  loading.value = false;
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });
    xTo.value = gsap.quickTo("#cursor", "x", {
      duration: 0.1,
      ease: "power3",
    });
    yTo.value = gsap.quickTo("#cursor", "y", {
      duration: 0.1,
      ease: "power3",
    });
  }
  window.addEventListener("resize", updateWidth);

	// tag v url parametru
	const tagParam = route.query.tag
  if (tagParam && tags.includes(tagParam)) {

		let filterTag = ''

		if (tagParam === 'networking') {
			filterTag = t('events.tag.networking')
		} else if (tagParam === 'workshop') {
			filterTag = t('events.tag.workshop')
		} else if (tagParam === 'public') {
			filterTag = t('public')
		} else if (tagParam === 'members') {
			filterTag = t('events.tag.events.membersOnly')
		}

		filteredTags.value = [filterTag]
    selectedTag.value = tagParam
		filterEvents()
    
		nextTick(() => {
			setTimeout(() => {
				scrollToItems();
			}, 1000);
		})
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
  window.document.removeEventListener("click", cursorOnClick.value);
});

const scrollToItems = () => {
  const el = document.getElementById('items')
  if (el) {
    const topPos = el.offsetTop
    window.scrollTo({
      top: topPos,
      behavior: 'smooth'
    })
  }
}

const mouseMove = (e) => {
  if (width.value > 1024 || matchMedia("(pointer:fine)").matches) {
    xTo.value(e.pageX);
    yTo.value(e.pageY);
  }
};
</script>

<template>
  <div @mousemove="mouseMove">
    <div class="overflow-hidden">
      <div class="navbar-wrapper">
        <Navbar
          :is-dark="true"
          @mouseover="
            setCursor(null, null, {
              'font-size': '14px',
              cursor: 'unset',
              'z-index': '1',
              width: '25px',
              height: '25px',
              opacity: 0.7,
            })
          "
        />
      </div>
      <div class="lg:relative">
        <Bubbles
          v-if="width > 1024"
          :key="width"
          class="w-full !absolute top-0"
          :height="getHeight"
          :start-width="700"
          :start-height="0"
          :set-cursor="setCursor"
          ref-prop="canvasEvents"
        />
        <div class="w-full pt-28 lg:pt-32">
          <div class="flex justify-start lg:justify-center">
            <div class="max-w-7xl w-[1280px] relative overflow-hidden">
              <div class="pb-12 px-0 sm:px-6">
                <AnimateTitle :title="$t('events.title')" />
              </div>
              <div class="w-full">
                <FeaturedEvent2
                  v-if="featuredEvent"
                  @click="cursorAction(featuredEvent.slug)"
                  :featured-item="[featuredEvent]"
                  :set-cursor="setCursor"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        @mouseenter="setCursor('', null, null)"
        class="flex justify-center pt-20 lg:pt-20 relative z-40"
				id="items"
      >
        <div
          class="max-w-7xl px-6 pb-6 lg:pb-20 flex gap-2 w-full flex-wrap relative z-40"
        >
          <button
            :class="{ 'active-tag': filteredTags.length === 0 }"
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm !font-normal"
            @click="resetFilter()"
          >
            {{ $t("events.allEvents") }}
          </button>
					<button
            :class="{
              'active-tag': filteredTags.includes($t('events.tag.networking')),
            }"
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm !font-normal"
            @click="toggleTag($t('events.tag.networking'))"
          >
            {{ $t("events.tag.networking") }}
          </button>
          <button
            :class="{
              'active-tag': filteredTags.includes($t('events.tag.workshop')),
            }"
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm !font-normal"
            @click="toggleTag($t('events.tag.workshop'))"
          >
            {{ $t("events.tag.workshop") }}
          </button>
          <button
            :class="{
              'active-tag': filteredTags.includes($t('public')),
            }"
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm !font-normal"
            @click="toggleTag($t('public'))"
          >
            {{ $t("events.tag.events.public") }}
          </button>
          <button
            :class="{
              'active-tag': filteredTags.includes(
                $t('events.tag.events.membersOnly')
              ),
            }"
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm !font-normal"
            @click="toggleTag($t('events.tag.events.membersOnly'))"
          >
            {{ $t("events.tag.events.membersOnly") }}
          </button>
          <div class="lg:ml-auto flex items-center">
            <NuxtLinkLocale
              class="underline font-light mr-6 uppercase"
              to="event-archive"
              >{{ $t("events.tag.events.pastEvents") }}</NuxtLinkLocale
            >
            <select
              class="bg-transparent border rounded-full border-black px-4 py-2 text-sm"
              v-model="selectedMonth"
              @change="filterMonth()"
            >
              <option value="0">{{ $t("select-month") }}</option>
              <option v-for="month in nextSixMonths" :key="month.value" :value="month.value">
								{{ month.label }}
							</option>
            </select>
          </div>
        </div>
      </div>
      <div
        @mouseenter="setCursor('', null, null)"
        class="flex justify-center relative"
      >
        <div class="max-w-7xl w-full px-6">
          <div
            v-if="
              events.filter((e) => e.eventId !== featuredEvent?.eventId)
                .length || loading
            "
          >
            <CalendarEvent
              v-for="event in filteredTags.length === 0
                ? events.filter((e) => e.eventId !== featuredEvent?.eventId)
                : events"
              :key="event.eventId"
              :data="event"
              class="transition-opacity duration-500"
              :class="{ 'opacity-20': loading, 'opacity-20': filtering }"
            />
            <div v-if="hasMoreEvents" class="flex justify-center">
              <div class="max-w-7xl px-16 mt-10 md:mt-20">
                <button
                  class="transition-opacity pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-4 py-2 text-sm"
                  :class="{ 'opacity-20': loading, 'opacity-20': filtering }"
                  :disabled="loading"
                  @click="loadMore()"
                >
                  <span>Načíst další</span>
                </button>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="flex justify-center">
              <p>
                {{ $t("reset-filter-text") }}
                <a
                  href="#"
                  rel="noopener noreferrer"
                  @click.prevent="resetFilter()"
                  class="underline hover:no-underline"
                  >{{ $t("reset-filter") }}</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>

      <BecomeMember class="md:mt-36 mt-14" :set-cursor="setCursor" />
      <Footer :set-cursor="setCursor" />
      <div>
        <div
          id="cursor"
          class="hidden pointer-events-none cursor-auto lg:cursor-none lg:flex"
          :style="cursorStyle"
          :class="{
            smallCursor: cursor === null,
            hidden: !width || width < 1024,
          }"
          v-html="cursor"
        ></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.featured-event-img {
  height: 670px;
}

#cursor {
  position: absolute;
  top: 0;
  left: -10px;
  width: 25px;
  height: 25px;
  display: none;
  font-weight: 400;

  justify-content: center;
  align-items: center;
  background-color: $color-primary;
  border-radius: 50%;
  z-index: 1000;
  cursor: none;
  transition:
    width 0.3s,
    height 0.3s,
    background-color 0.3s;
  transform: translate(-50%, -50%) translate(-150px, -150px);
  //pointer-events: none;
  text-align: center;
  @media screen {
    @media (pointer: fine) {
      display: flex;
    }
  }
}

.smallCursor {
  width: 20px;
  height: 20px;
  background-color: $color-primary;
}

.active-tag {
  background-color: $color-primary;
  color: black;
  border: white;
}

select {
  display: block;
  //width: 100%;
  width: 200px;
  font-size: 1em;
  //padding: 0.8rem 0.5rem;
  //padding: 0 12px;
  border: 1px solid #333;
  font-family: inherit;
  /** for the dropdown indicator */
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;

  font-family: Roboto, sans-serif;

  option {
    font-family: Roboto, sans-serif;
    border-radius: 23px;
  }
  .select-selected {
    font-family: Roboto, sans-serif;
  }
}
</style>
